// src/services/userService.ts

import { ApiError } from './apiError';
import { apiService } from './apiService';
import axios, { AxiosError } from 'axios';
import { ServiceResponse } from './serviceResponse';
import { UserPayload, Users } from '../dtos/User';

/** Params para /api/Usuarios/filter */
export interface UsuariosFilterParams {
  nomina?: string;
  nombre?: string;
  correo?: string;
  sucursalId?: number;
  activo?: boolean;
}

/** QS builder para el endpoint de filtro */
const buildQS = (p: UsuariosFilterParams) => {
  const qs = new URLSearchParams();
  if (p.nomina) qs.append('nomina', p.nomina);
  if (p.nombre) qs.append('nombre', p.nombre);
  if (p.correo) qs.append('correo', p.correo);
  if (p.sucursalId != null) qs.append('sucursalId', String(p.sucursalId));
  if (typeof p.activo === 'boolean') qs.append('activo', String(p.activo));
  return qs.toString();
};

/** Manejador de errores centralizado */
const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage = error.response?.data?.message || 'Ocurrió un error en el servidor.';
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: 'Ocurrió un error inesperado. Revisa tu conexión de red.' };
};

/** Obtiene la lista completa de usuarios. */
const getAll = async (): Promise<ServiceResponse<Users[]>> => {
  try {
    const users = await apiService.get<Users[]>('/api/Usuarios');
    return { data: users, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** Obtiene un usuario por su ID. */
const getById = async (id: number): Promise<ServiceResponse<Users>> => {
  try {
    const user = await apiService.get<Users>(`/api/Usuarios/${id}`);
    return { data: user, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** Crea un nuevo usuario. */
const create = async (payload: UserPayload): Promise<ServiceResponse<Users>> => {
  try {
    const newUser = await apiService.post<Users, UserPayload>('/api/Usuarios', payload);
    return { data: newUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** Actualiza un usuario existente. */
const update = async (id: number, payload: Partial<UserPayload>): Promise<ServiceResponse<Users>> => {
  try {
    const updatedUser = await apiService.put<Users, Partial<UserPayload>>(`/api/Usuarios/${id}`, payload);
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

const Delete = async (id: number, payload: Partial<UserPayload>): Promise<ServiceResponse<Users>> => {
  try {
    const updatedUser = await apiService.put<Users, Partial<UserPayload>>(`/api/Usuarios/${id}`, payload);
    return { data: updatedUser, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** Elimina un usuario. */
const remove = async (id: number): Promise<ServiceResponse<null>> => {
  try {
    await apiService.delete<void>(`/api/Usuarios/${id}`);
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/**
 * RF-02: Cambio de contraseña de un usuario por parte de un Administrador.
 * Llama al endpoint seguro del backend (valida política y registra auditoría).
 */
const changePassword = async (
  id: string | number,
  nuevaPassword: string,
): Promise<ServiceResponse<string>> => {
  try {
    const data = await apiService.patch<string, { nuevaPassword: string }>(
      `/api/Usuarios/${id}/password`,
      { nuevaPassword },
    );
    return { data, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

/** 🔎 Nuevo: obtiene usuarios filtrados (nomina, nombre, correo, sucursalId, activo) */
const getFiltered = async (
  params: UsuariosFilterParams
): Promise<ServiceResponse<Users[]>> => {
  try {
    const qs = buildQS(params);
    const url = `/api/Usuarios/filter${qs ? `?${qs}` : ''}`;
    const users = await apiService.get<Users[]>(url);
    return { data: users, error: null };
  } catch (err) {
    return { data: null, error: handleApiError(err) };
  }
};

export const userService = {
  getAll,
  getById,
  create,
  update,
  Delete,
  delete: remove,
  /** RF-02: cambio de contraseña por administrador */
  changePassword,
  /** exporta el filtro */
  getFiltered,
  /** exporta helpers por si los necesitas en otro lado */
  buildQS,
};
